import {
  FEEDBACK_FINGERPRINT,
  FEEDBACK_REASON_MAX,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  type FeedbackArea,
  type FeedbackGroup,
  type FeedbackGroupDraft,
  type FeedbackGroupItem,
  type FeedbackGrouping,
  type FeedbackSeverity,
  type FeedbackType,
  ISSUE_TEXT_FINDINGS,
  type IssuePreview,
  type IssueTextFindingKind,
  type OperatorFeedbackItem,
  type FeedbackStatus as Status,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ExternalLink, FolderKanban, Link2, Plus, X } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { EmptyState } from "@/components/EmptyState";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../api.ts";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { FeedbackStatus } from "../feedback/FeedbackStatus.tsx";
import { formatDateTime } from "../i18n/format.ts";
import { t as i18nT } from "../i18n/i18n.ts";
import {
  CONSOLE_FEEDBACK_GROUPS_PATH,
  CONSOLE_FEEDBACK_PATH,
  consoleFeedbackGroupPath,
  consoleFeedbackPath,
  Link,
  navigate,
} from "../router.tsx";
import { SettingsGroupTabs } from "../settings-shell/SettingsGroupTabs.tsx";
import {
  DraftButton,
  DraftNote,
  draftError,
  GroupLabelFields,
  useGroupDraft,
} from "./FeedbackAssist.tsx";
import { isOpenStatus } from "./feedback-filters.ts";
import { useCsrfHeaders } from "./operator.tsx";

/**
 * Feedback triage (openspec change `in-app-feedback` slice 3, spec `feedback-triage`): operators
 * group reports, set the status every reporter in the group sees, link client error
 * fingerprints, and create a GitLab issue from a preview of exactly what is sent. No report's
 * description appears here: it is read in the report itself, whose opening is audited.
 */

const groupsApi = api.api.operator["feedback-groups"];
const groupingApi = api.api.operator["feedback-grouping"];
const keys = {
  groups: ["console", "feedback", "groups"] as const,
  group: (id: string) => ["console", "feedback", "groups", id] as const,
  grouping: (reportId: string) => ["console", "feedback", "grouping", reportId] as const,
};

type FeedbackTab = "reports" | "groups";

/**
 * Reports and Groups, one URL each, under the Feedback page header, and the one line that says
 * what triage is for (feedback-triage-ux D3): reports go into groups, a group becomes one GitLab
 * issue, and its status is what every reporter in it reads.
 */
export function FeedbackTabs({ current }: { current: FeedbackTab }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <SettingsGroupTabs<FeedbackTab>
        label={t("console.feedback.tabs")}
        groups={["reports", "groups"]}
        current={current}
        href={(tab) => (tab === "reports" ? CONSOLE_FEEDBACK_PATH : CONSOLE_FEEDBACK_GROUPS_PATH)}
        groupLabel={(tab) =>
          t(tab === "reports" ? "console.feedback.tabReports" : "console.feedback.tabGroups")
        }
        count={() => 0}
        countLabel={() => ""}
      />
      <p className="max-w-prose text-sm text-fg-secondary">{t("console.feedback.triage.guide")}</p>
    </div>
  );
}

/** The way back from a report or a group: each step a link (feedback-triage-ux D2). */
export function FeedbackBreadcrumb({ steps }: { steps: { label: string; href: string }[] }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("project.breadcrumb")} className="text-sm">
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
        {steps.map((step, i) => (
          <li key={step.href} className="flex min-w-0 items-center gap-1.5">
            {i > 0 ? (
              <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
            ) : null}
            <Link
              href={step.href}
              className="min-w-0 break-words text-fg-secondary underline underline-offset-4 decoration-line-strong hover:text-fg hover:decoration-current"
            >
              {step.label}
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function useGroups() {
  return useQuery({
    queryKey: keys.groups,
    queryFn: async (): Promise<FeedbackGroupItem[]> => (await ok(await groupsApi.$get())).groups,
    retry: false,
  });
}

/** One group with its members (not audited: no description); the group page and its stepping. */
export function useFeedbackGroup(groupId: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: keys.group(groupId),
    queryFn: async (): Promise<FeedbackGroup> =>
      ok(await groupsApi[":groupId"].$get({ param: { groupId } })),
    retry: false,
  });
}

/** A report's group and the groups suggested for it (the report view's header and Group block). */
export function useReportGrouping(reportId: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: keys.grouping(reportId),
    queryFn: async (): Promise<FeedbackGrouping> =>
      ok(await groupingApi[":reportId"].$get({ param: { reportId } })),
    retry: false,
  });
}

/** "3 reports · 2 tenants · Issue #412", for a list row and the group's header. */
function groupMeta(group: FeedbackGroupItem): string {
  const t = i18nT;
  return [
    t(`feedback.types.${group.type}`),
    t("console.feedback.triage.counts", { count: group.reports }),
    t("console.feedback.triage.tenants", { count: group.tenants }),
    ...(group.issueNumber !== null
      ? [t("console.feedback.triage.issue", { number: group.issueNumber })]
      : []),
  ].join(" · ");
}

export function FeedbackGroups() {
  const { t } = useTranslation();
  const groups = useGroups();
  const [creating, setCreating] = useState(false);
  return (
    <Page width="wide">
      <PageHeader
        title={t("console.feedback.title")}
        lead={t("console.feedback.lead")}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("console.feedback.triage.newGroup")}
          </Button>
        }
      />
      <FeedbackTabs current="groups" />
      {groups.isError ? (
        <Notice tone="danger">{groups.error.message}</Notice>
      ) : !groups.data ? (
        <Skeleton className="h-48 w-full" />
      ) : groups.data.length === 0 ? (
        <EmptyState icon={FolderKanban} title={t("console.feedback.triage.groupsNone")}>
          {t("console.feedback.triage.groupsNoneHint")}
        </EmptyState>
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {groups.data.map((group) => (
            <li
              key={group.id}
              className="flex min-w-0 flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <Link
                  href={consoleFeedbackGroupPath(group.id)}
                  className="font-medium break-words text-fg underline-offset-4 hover:underline"
                >
                  {group.title}
                </Link>
                <span className="text-xs text-fg-secondary tabular">{groupMeta(group)}</span>
              </div>
              <FeedbackStatus report={group} />
            </li>
          ))}
        </ul>
      )}
      <NewGroupDialog open={creating} onClose={() => setCreating(false)} />
    </Page>
  );
}

/** A new group, optionally holding `reportIds` straight away; opens it once created. */
export function NewGroupDialog(props: {
  open: boolean;
  onClose: () => void;
  reportIds?: string[];
  type?: FeedbackType;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [type, setType] = useState<FeedbackType>(props.type ?? "bug");
  const create = useMutation({
    mutationFn: async () => {
      const res = await groupsApi.$post(
        { json: { title, type, summary: "", reportIds: props.reportIds ?? [] } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
      return (await res.json()).id;
    },
    onSuccess: async (groupId) => {
      await queryClient.invalidateQueries({ queryKey: ["console", "feedback"] });
      close();
      navigate(consoleFeedbackGroupPath(groupId));
    },
  });
  // Closing starts the next group afresh: no title carried over to a second, duplicate group.
  const close = () => {
    setTitle("");
    setType(props.type ?? "bug");
    create.reset();
    props.onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <AppDialog
      open={props.open}
      onClose={close}
      title={t("console.feedback.triage.newGroup")}
      description={t("console.feedback.triage.newGroupHint")}
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-title`}>{t("console.feedback.triage.titleLabel")}</Label>
          <Input
            id={`${id}-title`}
            value={title}
            maxLength={200}
            required
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-type`}>{t("console.feedback.triage.typeLabel")}</Label>
          <NativeSelect
            id={`${id}-type`}
            value={type}
            onChange={(e) => setType(e.currentTarget.value as FeedbackType)}
          >
            {FEEDBACK_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`feedback.types.${value}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
        {create.isError ? <Notice tone="danger">{create.error.message}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={close}>
            {t("console.feedback.triage.cancel")}
          </Button>
          <Button type="submit" disabled={!title.trim() || create.isPending}>
            {t("console.feedback.triage.create")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

function Section(props: {
  id: string;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={props.id} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={props.id} className="text-sm font-semibold text-fg">
            {props.title}
          </h2>
          {props.hint ? <p className="text-xs text-fg-secondary">{props.hint}</p> : null}
        </div>
        {props.action}
      </div>
      {props.children}
    </section>
  );
}

/** One group: its details and status, reports, fingerprints and the GitLab issue. */
export function FeedbackGroupPage({ groupId }: { groupId: string }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const group = useFeedbackGroup(groupId);
  const back = (
    <FeedbackBreadcrumb
      steps={[{ label: t("console.feedback.tabGroups"), href: CONSOLE_FEEDBACK_GROUPS_PATH }]}
    />
  );
  if (group.isError || !group.data) {
    return (
      <Page>
        {back}
        {group.isError ? (
          <Notice tone="danger">{group.error.message}</Notice>
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </Page>
    );
  }
  const data = group.data;
  return (
    <Page width="narrow" className="gap-8">
      <PageHeader
        before={back}
        title={<span className="break-words">{data.title}</span>}
        lead={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="tabular">{groupMeta(data)}</span>
            <FeedbackStatus report={data} />
          </span>
        }
      />
      {/* Keyed on the stored values: a change saved elsewhere refreshes the form (never a
          stale value saved back over it); "Saved" survives the remount through `saved`. */}
      <GroupDetails
        key={[
          data.id,
          data.title,
          data.summary,
          data.type,
          data.status,
          data.reason ?? "",
          data.severity ?? "",
          data.area ?? "",
        ].join("\u0000")}
        group={data}
        saved={saved}
        onSaved={setSaved}
      />
      <Section id="group-reports" title={t("console.feedback.triage.reports")}>
        {data.members.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            {t("console.feedback.triage.reportsNone")}{" "}
            <Link
              href={CONSOLE_FEEDBACK_PATH}
              className="font-medium text-fg underline underline-offset-4 decoration-line-strong hover:decoration-current"
            >
              {t("console.feedback.triage.goToReports")}
            </Link>
          </p>
        ) : (
          <ReportRows reports={data.members} groupId={groupId} action="remove" />
        )}
      </Section>
      {data.suggested.length > 0 ? (
        <Section
          id="group-suggested"
          title={t("console.feedback.triage.suggested")}
          hint={t("console.feedback.triage.suggestedHint")}
        >
          <ReportRows reports={data.suggested} groupId={groupId} action="add" />
        </Section>
      ) : null}
      <GitLabIssue group={data} />
      <Fingerprints group={data} />
    </Page>
  );
}

function GroupDetails({
  group,
  saved,
  onSaved,
}: {
  group: FeedbackGroup;
  saved: boolean;
  onSaved: (saved: boolean) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(group.title);
  const [summary, setSummary] = useState(group.summary);
  const [type, setType] = useState<FeedbackType>(group.type);
  const [status, setStatus] = useState<Status>(group.status);
  // An older API has no reason: the field stays hidden (feedback-reporter-status D5).
  const storedReason = group.reason;
  const [reason, setReason] = useState(storedReason ?? "");
  // Shown with "Not planned", or while a reason is stored; only a shown field is ever sent.
  const reasonShown = storedReason !== undefined && (status === "not_planned" || !!storedReason);
  const reasonChanged = reasonShown && reason.trim() !== storedReason;
  // Severity and area (feedback-triage-assist): hidden for an older API, which has neither.
  const labelsShown = group.severity !== undefined;
  const [severity, setSeverity] = useState<FeedbackSeverity | null>(group.severity ?? null);
  const [area, setArea] = useState<FeedbackArea | null>(group.area ?? null);
  const severityChanged = labelsShown && severity !== (group.severity ?? null);
  const areaChanged = labelsShown && area !== (group.area ?? null);
  // The assistant's draft fills the fields for editing; tinted until edited, never saved by it.
  const [draft, setDraft] = useState<FeedbackGroupDraft | null>(null);
  const [drafted, setDrafted] = useState({
    title: false,
    summary: false,
    severity: false,
    area: false,
  });
  const drafting = useGroupDraft(group.id, (next) => {
    onSaved(false);
    setDraft(next);
    setTitle(next.title);
    setSummary(next.summary);
    setSeverity(next.severity);
    setArea(next.area);
    setDrafted({ title: true, summary: true, severity: true, area: true });
  });
  const discardDraft = () => {
    setDraft(null);
    setTitle(group.title);
    setSummary(group.summary);
    setSeverity(group.severity ?? null);
    setArea(group.area ?? null);
    setDrafted({ title: false, summary: false, severity: false, area: false });
  };
  const tint = (on: boolean) => (on ? "bg-accent-subtle" : undefined);
  const edit = <T,>(set: (value: T) => void, value: T) => {
    onSaved(false);
    set(value);
  };
  const changed =
    title.trim() !== group.title ||
    summary.trim() !== group.summary ||
    type !== group.type ||
    status !== group.status ||
    reasonChanged ||
    severityChanged ||
    areaChanged;
  const save = useMutation({
    mutationFn: async () => {
      const res = await groupsApi[":groupId"].$patch(
        {
          param: { groupId: group.id },
          json: {
            ...(title.trim() !== group.title && { title }),
            ...(summary.trim() !== group.summary && { summary }),
            ...(type !== group.type && { type }),
            ...(status !== group.status && { status }),
            // Only when changed: an older API refuses the key.
            ...(reasonChanged && { reason }),
            ...(severityChanged && { severity }),
            ...(areaChanged && { area }),
          },
        },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: async () => {
      onSaved(true);
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ["console", "feedback"] });
    },
  });
  // A refused reason names the kinds found (never an excerpt: it may be a reporter's words).
  const reasonFindings =
    save.error instanceof ApiError && save.error.code === "reason_text_flagged"
      ? String(save.error.details?.findings ?? "")
          .split(",")
          .filter((k): k is IssueTextFindingKind =>
            (ISSUE_TEXT_FINDINGS as readonly string[]).includes(k),
          )
      : [];
  return (
    <Section
      id="group-details"
      title={t("console.feedback.triage.details")}
      hint={t("console.feedback.triage.detailsHint")}
      action={
        labelsShown ? (
          <DraftButton pending={drafting.isPending} onClick={() => drafting.mutate()} />
        ) : null
      }
    >
      <form
        className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-title`}>{t("console.feedback.triage.titleLabel")}</Label>
          <Input
            id={`${id}-title`}
            value={title}
            maxLength={200}
            required
            aria-describedby={`${id}-title-hint`}
            className={tint(drafted.title)}
            onChange={(e) => {
              setDrafted((d) => ({ ...d, title: false }));
              edit(setTitle, e.currentTarget.value);
            }}
          />
          <p id={`${id}-title-hint`} className="text-xs text-fg-secondary">
            {t("console.feedback.triage.titleHint")}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-summary`}>{t("console.feedback.triage.summaryLabel")}</Label>
          <Textarea
            id={`${id}-summary`}
            value={summary}
            rows={4}
            maxLength={4000}
            aria-describedby={`${id}-summary-hint`}
            className={tint(drafted.summary)}
            onChange={(e) => {
              setDrafted((d) => ({ ...d, summary: false }));
              edit(setSummary, e.currentTarget.value);
            }}
          />
          <p id={`${id}-summary-hint`} className="text-xs text-fg-secondary">
            {t("console.feedback.triage.summaryHint")}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-type`}>{t("console.feedback.triage.typeLabel")}</Label>
            <NativeSelect
              id={`${id}-type`}
              value={type}
              aria-describedby={`${id}-type-hint`}
              onChange={(e) => edit(setType, e.currentTarget.value as FeedbackType)}
            >
              {FEEDBACK_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`feedback.types.${value}`)}
                </option>
              ))}
            </NativeSelect>
            <p id={`${id}-type-hint`} className="text-xs text-fg-secondary">
              {t("console.feedback.triage.typeHint")}
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-status`}>{t("console.feedback.triage.statusLabel")}</Label>
            <NativeSelect
              id={`${id}-status`}
              value={status}
              aria-describedby={`${id}-status-hint`}
              onChange={(e) => edit(setStatus, e.currentTarget.value as Status)}
            >
              {FEEDBACK_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`myFeedback.statuses.${value}`)}
                </option>
              ))}
            </NativeSelect>
            {/* The selected status, explained: what reporters read and whether it closes the
                group (feedback-triage-ux D5). The select's description, so no live region. */}
            <p id={`${id}-status-hint`} className="text-xs text-fg-secondary">
              {t(`console.feedback.triage.statusHelp.${status}`)}
              {/* `new` notifies nobody (feedback-reporter-status). */}
              {status === "new" ? null : ` ${t("console.feedback.triage.statusNotified")}`}
            </p>
          </div>
        </div>
        {labelsShown ? (
          <GroupLabelFields
            id={id}
            severity={severity}
            area={area}
            drafted={drafted}
            onSeverity={(value) => {
              setDrafted((d) => ({ ...d, severity: false }));
              edit(setSeverity, value);
            }}
            onArea={(value) => {
              setDrafted((d) => ({ ...d, area: false }));
              edit(setArea, value);
            }}
          />
        ) : null}
        {draft ? <DraftNote draft={draft} /> : null}
        {drafting.isError ? <Notice tone="warning">{draftError(drafting.error)}</Notice> : null}
        {reasonShown ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-reason`}>{t("console.feedback.triage.reasonLabel")}</Label>
            <Textarea
              id={`${id}-reason`}
              value={reason}
              rows={2}
              maxLength={FEEDBACK_REASON_MAX}
              aria-describedby={`${id}-reason-hint`}
              aria-invalid={reasonFindings.length > 0 || undefined}
              onChange={(e) => edit(setReason, e.currentTarget.value)}
            />
            <p id={`${id}-reason-hint`} className="text-xs text-fg-secondary">
              {t("console.feedback.triage.reasonHint")}
            </p>
          </div>
        ) : null}
        {reasonFindings.length > 0 ? (
          <Notice tone="warning" title={t("console.feedback.triage.reasonFindingsTitle")}>
            <ul className="list-disc pl-4">
              {reasonFindings.map((kind) => (
                <li key={kind}>{t(`console.feedback.triage.reasonFindings.${kind}`)}</li>
              ))}
            </ul>
          </Notice>
        ) : save.isError ? (
          <Notice tone="danger">{save.error.message}</Notice>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          {saved && !changed ? (
            <span role="status" className="text-xs text-fg-secondary">
              {t("console.feedback.triage.saved")}
            </span>
          ) : null}
          {draft ? (
            <Button type="button" variant="ghost" onClick={discardDraft}>
              {t("console.feedback.assist.discardDraft")}
            </Button>
          ) : null}
          <Button type="submit" disabled={!changed || !title.trim() || save.isPending}>
            {t("console.feedback.triage.save")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

export function useAssign(onDone?: () => void) {
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reportIds: string[]; groupId: string | null }) => {
      const res = await groupingApi.$put({ json: input }, headers);
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["console", "feedback"] });
      onDone?.();
    },
  });
}

function ReportRows(props: {
  reports: OperatorFeedbackItem[];
  groupId: string;
  action: "add" | "remove";
}) {
  const { t } = useTranslation();
  const assign = useAssign();
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
        {props.reports.map((report) => {
          const label = t("console.feedback.rowTitle", {
            type: t(`feedback.types.${report.type}`),
            tenant: report.tenantName,
          });
          return (
            <li
              key={report.id}
              className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <Link
                  href={consoleFeedbackPath(report.id, { group: props.groupId })}
                  className="font-medium text-fg underline-offset-4 hover:underline"
                >
                  {label}
                </Link>
                <span className="truncate font-mono text-xs text-fg-muted">
                  {[report.route, report.version, report.build].filter(Boolean).join(" · ") ||
                    t("console.feedback.noContext")}
                </span>
              </div>
              <time dateTime={report.createdAt} className="text-xs text-fg-muted tabular">
                {formatDateTime(report.createdAt)}
              </time>
              <Button
                variant="ghost"
                size="sm"
                disabled={assign.isPending}
                aria-label={`${t(
                  props.action === "add"
                    ? "console.feedback.triage.add"
                    : "console.feedback.triage.removeReport",
                )}: ${label}`}
                onClick={() =>
                  assign.mutate({
                    reportIds: [report.id],
                    groupId: props.action === "add" ? props.groupId : null,
                  })
                }
              >
                {props.action === "add" ? <Plus aria-hidden="true" /> : <X aria-hidden="true" />}
                {t(
                  props.action === "add"
                    ? "console.feedback.triage.add"
                    : "console.feedback.triage.removeReport",
                )}
              </Button>
            </li>
          );
        })}
      </ul>
      {assign.isError ? <Notice tone="danger">{assign.error.message}</Notice> : null}
    </div>
  );
}

function Fingerprints({ group }: { group: FeedbackGroup }) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const done = () => queryClient.invalidateQueries({ queryKey: ["console", "feedback"] });
  const link = useMutation({
    mutationFn: async (fingerprint: string) => {
      const res = await groupsApi[":groupId"].fingerprints.$post(
        { param: { groupId: group.id }, json: { fingerprint } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: async () => {
      setValue("");
      await done();
    },
  });
  const unlink = useMutation({
    mutationFn: async (fingerprint: string) => {
      const res = await groupsApi[":groupId"].fingerprints[":fingerprint"].$delete(
        { param: { groupId: group.id, fingerprint } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: done,
  });
  const candidate = value.trim().toLowerCase();
  const error = link.error ?? unlink.error;
  return (
    <Section
      id="group-fingerprints"
      title={t("console.feedback.triage.fingerprints")}
      hint={t("console.feedback.triage.fingerprintsHint")}
    >
      {group.fingerprints.length === 0 ? (
        <p className="text-sm text-fg-secondary">{t("console.feedback.triage.fingerprintsNone")}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {group.fingerprints.map((fingerprint) => (
            <li
              key={fingerprint}
              className="flex items-center justify-between gap-3 py-1.5 pr-1.5 pl-4"
            >
              <code className="font-mono text-xs text-fg">{fingerprint}</code>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={unlink.isPending}
                aria-label={t("console.feedback.triage.unlink", { fingerprint })}
                onClick={() => unlink.mutate(fingerprint)}
              >
                <X aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-col gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          link.mutate(candidate);
        }}
      >
        <Label htmlFor={`${id}-fingerprint`}>{t("console.feedback.triage.fingerprintLabel")}</Label>
        <div className="flex gap-2">
          <Input
            id={`${id}-fingerprint`}
            value={value}
            spellCheck={false}
            autoComplete="off"
            placeholder="3fa9c1d2e4b5a6f7"
            className="max-w-56 font-mono text-xs"
            onChange={(e) => setValue(e.currentTarget.value)}
          />
          <Button
            type="submit"
            variant="outline"
            disabled={!FEEDBACK_FINGERPRINT.test(candidate) || link.isPending}
          >
            <Link2 aria-hidden="true" />
            {t("console.feedback.triage.link")}
          </Button>
        </div>
      </form>
      {error ? <Notice tone="danger">{error.message}</Notice> : null}
    </Section>
  );
}

function GitLabIssue({ group }: { group: FeedbackGroup }) {
  const { t } = useTranslation();
  const [previewing, setPreviewing] = useState(false);
  const tracker = useQuery({
    queryKey: ["console", "feedback", "tracker"] as const,
    queryFn: async () => (await ok(await api.api.operator["feedback-tracker"].$get())).status,
    retry: false,
    enabled: group.issueNumber === null,
  });
  return (
    <Section
      id="group-gitlab"
      title={t("console.feedback.triage.gitlab")}
      hint={t("console.feedback.triage.gitlabHint")}
    >
      {group.issueNumber === null ? (
        <p className="text-xs text-fg-secondary">{t("console.feedback.triage.previewChecks")}</p>
      ) : null}
      {group.issueNumber !== null ? (
        group.issueUrl ? (
          <a
            href={group.issueUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-accent-text underline-offset-4 hover:underline"
          >
            {t("console.feedback.triage.openIssue", { number: group.issueNumber })}
            <ExternalLink aria-hidden="true" className="size-3.5" />
          </a>
        ) : (
          <p className="text-sm text-fg tabular">
            {t("console.feedback.triage.issue", { number: group.issueNumber })}
          </p>
        )
      ) : tracker.data === "not_configured" ? (
        <Notice title={t("console.feedback.triage.notConfigured")}>
          {t("console.feedback.triage.notConfiguredHint")}
        </Notice>
      ) : tracker.data === "unavailable" ? (
        <Notice tone="warning">{t("console.feedback.triage.unavailable")}</Notice>
      ) : tracker.isError ? (
        <Notice tone="danger">{tracker.error.message}</Notice>
      ) : (
        <Button
          variant="outline"
          className="self-start"
          disabled={!tracker.data}
          onClick={() => setPreviewing(true)}
        >
          {t("console.feedback.triage.preview")}
        </Button>
      )}
      <IssuePreviewDialog
        groupId={group.id}
        open={previewing}
        onClose={() => setPreviewing(false)}
      />
    </Section>
  );
}

/** The exact issue, what refuses it, and the one confirmation that sends it. */
function IssuePreviewDialog(props: { groupId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const previewKey = ["console", "feedback", "issue", props.groupId] as const;
  const preview = useQuery({
    queryKey: previewKey,
    queryFn: async (): Promise<IssuePreview> =>
      ok(await groupsApi[":groupId"].issue.$get({ param: { groupId: props.groupId } })),
    enabled: props.open,
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const create = useMutation({
    mutationFn: async (digest: string) => {
      const res = await groupsApi[":groupId"].issue.$post(
        { param: { groupId: props.groupId }, json: { digest } },
        headers,
      );
      if (res.status === 502) {
        // The API's own refusal is JSON; a 502 from the edge may be an HTML page.
        const body = (await res.json().catch(() => null)) as {
          code?: unknown;
          status?: number;
        } | null;
        if (body?.code !== "tracker_failed") {
          throw await apiError({ status: res.status, json: async () => body });
        }
        throw new ApiError(
          502,
          t("console.feedback.triage.trackerFailed", {
            status: body.status ? ` (${body.status})` : "",
          }),
          "tracker_failed",
          true,
        );
      }
      if (!res.ok) {
        throw await apiError(res);
      }
      return res.json();
    },
    onSuccess: async () => {
      // Forget the preview first: refetching it would read (and audit) the reports again.
      queryClient.removeQueries({ queryKey: previewKey });
      close();
      await queryClient.invalidateQueries({ queryKey: ["console", "feedback"] });
    },
    onError: (error) => {
      // The server re-rendered a different issue: show the new one before another confirm.
      if (error instanceof ApiError && error.code === "preview_changed") {
        void queryClient.invalidateQueries({ queryKey: previewKey });
      }
    },
  });
  // Closing forgets the preview and the last attempt, so reopening never shows (or confirms)
  // an issue rendered before the group changed.
  const close = () => {
    queryClient.removeQueries({ queryKey: previewKey });
    create.reset();
    props.onClose();
  };
  const data = preview.data;
  const blocked =
    !data || preview.isFetching || data.findings.length > 0 || data.tracker !== "configured";
  return (
    <AppDialog
      open={props.open}
      onClose={close}
      title={t("console.feedback.triage.previewTitle")}
      description={t("console.feedback.triage.previewLead")}
    >
      {preview.isError ? (
        <Notice tone="danger">{preview.error.message}</Notice>
      ) : !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {data.findings.length > 0 ? (
            <Notice tone="warning" title={t("console.feedback.triage.findingsTitle")}>
              <ul className="flex flex-col gap-1">
                {data.findings.map((f) => (
                  <li key={`${f.field}-${f.kind}-${f.excerpt}`} className="break-words">
                    <span className="font-medium">
                      {t(`console.feedback.triage.fields.${f.field}`)}
                    </span>
                    {" · "}
                    {t(`console.feedback.triage.findings.${f.kind}`, { excerpt: f.excerpt })}
                  </li>
                ))}
              </ul>
            </Notice>
          ) : null}
          {data.tracker === "not_configured" ? (
            <Notice title={t("console.feedback.triage.notConfigured")}>
              {t("console.feedback.triage.notConfiguredHint")}
            </Notice>
          ) : null}
          <dl className="flex min-w-0 flex-col gap-3 text-sm">
            <div className="flex flex-col gap-0.5">
              <dt className="text-xs text-fg-secondary">
                {t("console.feedback.triage.previewIssueTitle")}
              </dt>
              <dd className="font-medium break-words text-fg">{data.title}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-xs text-fg-secondary">{t("console.feedback.triage.labels")}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {data.labels.map((label) => (
                  <code
                    key={label}
                    className="rounded-sm border border-line bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-fg-secondary"
                  >
                    {label}
                  </code>
                ))}
              </dd>
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <dt className="text-xs text-fg-secondary">{t("console.feedback.triage.body")}</dt>
              <dd className="min-w-0">
                {/* As GitLab receives it: plain text, never rendered here. */}
                <pre
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region keyboard users must reach
                  tabIndex={0}
                  className="max-h-72 overflow-auto rounded-md border border-line bg-surface-sunken p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words text-fg"
                >
                  {data.body}
                </pre>
              </dd>
            </div>
          </dl>
          {create.isError ? <Notice tone="danger">{create.error.message}</Notice> : null}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2 pt-2">
        <Button variant="outline" onClick={close}>
          {t("console.feedback.triage.cancel")}
        </Button>
        <Button
          disabled={blocked || create.isPending}
          onClick={() => data && create.mutate(data.digest)}
        >
          {t("console.feedback.triage.confirm")}
        </Button>
      </div>
    </AppDialog>
  );
}

/**
 * The report view's place in triage, after the evidence (feedback-triage-ux D4): its group (a
 * link) and taking it out, the groups suggested by a fingerprint of its recent errors, and adding
 * it to an open group (or moving it to another one) or to a new group.
 */
export function ReportGrouping({ reportId, type }: { reportId: string; type: FeedbackType }) {
  const { t } = useTranslation();
  const id = useId();
  const grouping = useReportGrouping(reportId);
  const groups = useGroups();
  const assign = useAssign();
  const current = grouping.data?.group ?? null;
  // A fingerprint stays linked to a closed group: once the list is known, a closed group is not
  // suggested (it would reopen nothing and hide the report from triage).
  const closed = new Set(
    (groups.data ?? []).filter((g) => !isOpenStatus(g.status)).map((g) => g.id),
  );
  const suggestions = (grouping.data?.suggestions ?? []).filter((s) => !closed.has(s.id));
  return (
    <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-3">
      <h2 id={`${id}-heading`} className="text-sm font-semibold text-fg">
        {t("console.feedback.triage.group")}
      </h2>
      {grouping.isError ? (
        <Notice tone="danger">{grouping.error.message}</Notice>
      ) : !grouping.data ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <div className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4">
          {current ? (
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 text-sm">
              <div className="flex min-w-0 flex-col gap-0.5">
                <Link
                  href={consoleFeedbackGroupPath(current.id)}
                  className="min-w-0 font-medium break-words text-fg underline underline-offset-4 decoration-line-strong hover:decoration-current"
                >
                  {current.title}
                </Link>
                <p id={`${id}-remove-hint`} className="text-xs text-fg-secondary">
                  {t("console.feedback.triage.removeHint")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={assign.isPending}
                aria-describedby={`${id}-remove-hint`}
                onClick={() => assign.mutate({ reportIds: [reportId], groupId: null })}
              >
                <X aria-hidden="true" />
                {t("console.feedback.triage.ungroup")}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-fg-secondary">
              {t("console.feedback.triage.notGroupedHint")}
            </p>
          )}
          {suggestions.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {suggestions.map((s) => (
                <li
                  key={`${s.id}-${s.fingerprint}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-sunken px-3 py-2 text-sm"
                >
                  <span className="min-w-0 break-words text-fg-secondary">
                    {t("console.feedback.triage.suggestion", { title: s.title })}{" "}
                    <code className="font-mono text-xs text-fg-muted">{s.fingerprint}</code>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={assign.isPending}
                    onClick={() => assign.mutate({ reportIds: [reportId], groupId: s.id })}
                  >
                    {t("console.feedback.triage.add")}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <AddToGroup
            assign={assign}
            reportIds={[reportId]}
            currentId={current?.id ?? null}
            type={type}
            label={t(
              current ? "console.feedback.triage.moveToOther" : "console.feedback.triage.addTo",
            )}
            submitLabel={t(
              current ? "console.feedback.triage.move" : "console.feedback.triage.addShort",
            )}
          />
          {assign.isError ? <Notice tone="danger">{assign.error.message}</Notice> : null}
        </div>
      )}
    </section>
  );
}

/**
 * Adding reports to an open group, or to a new group (feedback-triage-ux D4): the report view
 * for one report, the inbox's selection toolbar for several (feedback-inbox-followups D4). One
 * request moves them all; no report is read.
 */
export function AddToGroup(props: {
  reportIds: string[];
  /** The reports' current group, left out of the choices (the report view). */
  currentId: string | null;
  /** The new group's type, preselected. */
  type: FeedbackType;
  label: string;
  /** The label for assistive technology only (the inbox toolbar, whose count line says it). */
  hideLabel?: boolean;
  submitLabel: string;
  onDone?: () => void;
  /** The caller's own move, so one request at a time across its actions (the report view). */
  assign?: ReturnType<typeof useAssign>;
  className?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const groups = useGroups();
  const own = useAssign(props.onDone);
  const assign = props.assign ?? own;
  const [target, setTarget] = useState("");
  const [creating, setCreating] = useState(false);
  // Open groups only: a closed one would reopen nothing and hide the reports from triage.
  const choices = (groups.data ?? []).filter(
    (g) => g.id !== props.currentId && isOpenStatus(g.status),
  );
  return (
    <div className={props.className ?? "flex flex-col gap-1.5"}>
      <form
        className="flex flex-col gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (target) {
            assign.mutate({ reportIds: props.reportIds, groupId: target });
            setTarget("");
          }
        }}
      >
        <Label htmlFor={`${id}-target`} className={props.hideLabel ? "sr-only" : undefined}>
          {props.label}
        </Label>
        <div className="flex flex-wrap gap-2">
          <NativeSelect
            id={`${id}-target`}
            value={target}
            className="max-w-sm"
            onChange={(e) => setTarget(e.currentTarget.value)}
          >
            <option value="">
              {t(
                !groups.data || choices.length > 0
                  ? "console.feedback.triage.chooseGroup"
                  : props.currentId
                    ? "console.feedback.triage.noOtherOpenGroup"
                    : "console.feedback.triage.noOpenGroup",
              )}
            </option>
            {choices.map((g) => (
              <option key={g.id} value={g.id}>
                {[
                  g.title,
                  t(`feedback.types.${g.type}`),
                  t("console.feedback.triage.counts", { count: g.reports }),
                ].join(" · ")}
              </option>
            ))}
          </NativeSelect>
          <Button type="submit" variant="outline" disabled={!target || assign.isPending}>
            {props.submitLabel}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("console.feedback.triage.newGroup")}
          </Button>
        </div>
      </form>
      {!props.assign && assign.isError ? (
        <Notice tone="danger">{assign.error.message}</Notice>
      ) : null}
      {/* Keyed on the type: a selection whose common type changed preselects the new one. */}
      <NewGroupDialog
        key={props.type}
        open={creating}
        onClose={() => setCreating(false)}
        reportIds={props.reportIds}
        type={props.type}
      />
    </div>
  );
}

/**
 * The report's stored error fingerprints, under its recent errors (feedback-inbox-followups D3):
 * each says whether it is linked to the report's group, to another group (named), or to none;
 * in a group, an unlinked one offers "Link to this group", as the group page links it. The
 * fingerprint of an error is computed on the server, so they are listed apart from the errors.
 */
export function ReportFingerprints({
  reportId,
  fingerprints,
}: {
  reportId: string;
  fingerprints: readonly string[];
}) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const grouping = useReportGrouping(reportId);
  const group = grouping.data?.group ?? null;
  // Absent from an older API: no link state to show, so no action either.
  const links = grouping.data?.fingerprintGroups;
  const link = useMutation({
    mutationFn: async (input: { groupId: string; fingerprint: string }) => {
      const res = await groupsApi[":groupId"].fingerprints.$post(
        { param: { groupId: input.groupId }, json: { fingerprint: input.fingerprint } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    // Narrow refreshes: never the report itself, whose every read is an audited opening. A
    // fingerprint linked elsewhere meanwhile (`409`) shows that group once re-read.
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.grouping(reportId) }),
        queryClient.invalidateQueries({ queryKey: keys.groups }),
      ]);
    },
  });
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-medium text-fg-secondary">
        {t("console.feedback.triage.reportFingerprints")}
      </h3>
      <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
        {fingerprints.map((fingerprint) => {
          const linked = links?.find((l) => l.fingerprint === fingerprint);
          return (
            <li
              key={fingerprint}
              className="flex min-h-10 flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5 pr-1.5 pl-4 text-sm"
            >
              <code className="font-mono text-xs text-fg">{fingerprint}</code>
              {!links ? null : linked && linked.groupId === group?.id ? (
                <span className="text-xs text-fg-secondary">
                  {t("console.feedback.triage.linkedHere")}
                </span>
              ) : linked ? (
                <span className="min-w-0 text-xs break-words text-fg-secondary">
                  {t("console.feedback.triage.linkedTo")}{" "}
                  <Link
                    href={consoleFeedbackGroupPath(linked.groupId)}
                    className="font-medium text-fg underline underline-offset-4 decoration-line-strong hover:decoration-current"
                  >
                    {linked.title}
                  </Link>
                </span>
              ) : group ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={link.isPending}
                  aria-label={t("console.feedback.triage.linkHereLabel", { fingerprint })}
                  onClick={() => link.mutate({ groupId: group.id, fingerprint })}
                >
                  <Link2 aria-hidden="true" />
                  {t("console.feedback.triage.linkHere")}
                </Button>
              ) : (
                <span className="text-xs text-fg-muted">
                  {t("console.feedback.triage.notLinked")}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {link.isError ? <Notice tone="danger">{link.error.message}</Notice> : null}
    </div>
  );
}
